/**
 * MODULE: _shared/frontend-shell/src/components/billing/SubscriptionRenewBanner.tsx
 * PURPOSE: App-shell top banner when AI tokens are low or exhausted (“time to renew”)
 * DEPENDENCIES: react-router-dom, useShellRenewBanner, assistantQuotaGuidance paths/copy
 * PUBLIC_API: SubscriptionRenewBanner
 * SIDE_EFFECTS: Navigates to settings; session dismiss via hook
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: ~110 / 200 lines
 * RECENT: 2026-08-06 [2.2.2133] — Global renew banner for three billing flows
 */
import { Link, useLocation } from 'react-router-dom';

import {
  ASSISTANT_QUOTA_BILLING_PATH,
  ASSISTANT_QUOTA_SETTINGS_PATH,
  shellRenewBannerCopyForSeverity,
  type AssistantQuotaSeverity,
} from '../assistant/assistantQuotaGuidance';
import { useShellRenewBanner } from '../../hooks/billing/useShellRenewBanner';

const SHELL: Record<AssistantQuotaSeverity, string> = {
  warning:
    'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100',
  critical:
    'border-orange-400 bg-orange-50 text-orange-950 dark:border-orange-700 dark:bg-orange-950/45 dark:text-orange-100',
  exceeded:
    'border-red-400 bg-red-50 text-red-950 dark:border-red-700 dark:bg-red-950/40 dark:text-red-100',
};

/** Hide on renew destinations so the page itself owns the CTA. */
function isRenewDestinationPath(pathname: string): boolean {
  return (
    pathname === ASSISTANT_QUOTA_SETTINGS_PATH ||
    pathname.startsWith(`${ASSISTANT_QUOTA_SETTINGS_PATH}/`) ||
    pathname === ASSISTANT_QUOTA_BILLING_PATH ||
    pathname.startsWith(`${ASSISTANT_QUOTA_BILLING_PATH}/`)
  );
}

export function SubscriptionRenewBanner() {
  const location = useLocation();
  const onDestination = isRenewDestinationPath(location.pathname);
  const { visible, severity, percentUsed, dismiss } = useShellRenewBanner(!onDestination);

  if (!visible || onDestination) return null;

  const testId =
    severity === 'exceeded' ? 'shell-renew-banner-exceeded' : 'shell-renew-banner-warning';

  return (
    <div
      data-testid={testId}
      data-quota-severity={severity}
      role="status"
      aria-live="polite"
      className={`shrink-0 border-b px-4 py-2.5 sm:px-6 ${SHELL[severity]}`}
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="text-sm font-medium leading-snug">
          {shellRenewBannerCopyForSeverity(severity, percentUsed)}
        </p>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Link
            to={ASSISTANT_QUOTA_SETTINGS_PATH}
            className="rounded-md border border-current/30 bg-white/90 px-3 py-1.5 text-xs font-semibold hover:bg-white dark:bg-black/25 dark:hover:bg-black/40"
            data-testid="shell-renew-cta-topup"
          >
            Top up credits
          </Link>
          <Link
            to={ASSISTANT_QUOTA_BILLING_PATH}
            className="rounded-md border border-current/30 bg-white/90 px-3 py-1.5 text-xs font-semibold hover:bg-white dark:bg-black/25 dark:hover:bg-black/40"
            data-testid="shell-renew-cta-billing"
          >
            Billing &amp; plans
          </Link>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md px-2 py-1.5 text-xs font-medium opacity-80 hover:underline"
            data-testid="shell-renew-dismiss"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
